import { FC, useEffect, useMemo, useState } from 'react'
import { Box, Card, CardContent, Tab, Tabs } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import { getInventoryLogsAsync } from 'src/redux/actions/inventoryActions'
import { getPriceLogsAsync } from 'src/redux/actions/priceLogsActions'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import InventoryLogs from '../inventory/InventoryLogs'
import PriceLogs from '../price-logs/PriceLogs'
import { logsApiResultHasRecords } from './nonOmsProductDetailVisibility'

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: '#007BFF',
    height: 3,
    borderRadius: 2,
  },
})

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  color: '#007BFF',
  '&.Mui-selected': {
    color: '#007BFF',
  },
  '&:hover': {
    color: '#0056b3',
  },
  minHeight: 48,
  marginRight: theme.spacing(2),
}))

type LogsProbeState = {
  loading: boolean
  showInventory: boolean
  showPrice: boolean
  /** When probe fails, show both tabs so existing log UI still works. */
  probeFailed: boolean
}

const initialProbeState = (): LogsProbeState => ({
  loading: true,
  showInventory: false,
  showPrice: false,
  probeFailed: false,
})

type NonOmsProductLogsSectionProps = {
  variantId?: string
}

export const NonOmsProductLogsSection: FC<NonOmsProductLogsSectionProps> = ({ variantId }) => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const routePlatformId = params?.platformId ?? ''
  const resolvedVariantId = variantId || params?.variantId
  const { fetchedConnectedPlatformData, fetchedConnectedPlatformLoading } = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )

  const [probe, setProbe] = useState<LogsProbeState>(initialProbeState)
  const [activeTabKey, setActiveTabKey] = useState<'inventory' | 'price'>('inventory')

  useEffect(() => {
    let cancelled = false

    if (!resolvedVariantId || !routePlatformId) {
      setProbe({
        loading: false,
        showInventory: false,
        showPrice: false,
        probeFailed: false,
      })
      return () => {
        cancelled = true
      }
    }

    if (fetchedConnectedPlatformLoading || fetchedConnectedPlatformData.length === 0) {
      setProbe((prev) => ({ ...prev, loading: true }))
      return () => {
        cancelled = true
      }
    }

    setProbe(initialProbeState())

    const inventoryParams = {
      page_num: 1,
      page_limit: 1,
      search_string: '',
      status: '',
      sort_by: 'api_created_at',
      sort_order: 'DESC',
      from_date: '',
      to_date: '',
      location: '',
      platform_id: routePlatformId,
      variant_id: resolvedVariantId,
    }

    const priceParams = {
      page_num: 1,
      page_limit: 1,
      search_string: '',
      status: '',
      sort_by: 'pc_created_at',
      sort_order: 'DESC',
      from_date: '',
      to_date: '',
      platform_id: routePlatformId,
      variant_id: resolvedVariantId,
    }

    const runProbe = async () => {
      try {
        const [inventoryResult, priceResult] = await Promise.all([
          dispatch(getInventoryLogsAsync({ paramData: inventoryParams })).unwrap(),
          dispatch(getPriceLogsAsync({ paramData: priceParams })).unwrap(),
        ])

        if (cancelled) return

        const showInventory = logsApiResultHasRecords(
          inventoryResult?.data?.total_items,
          inventoryResult?.data?.inventory_logs
        )
        const showPrice = logsApiResultHasRecords(
          priceResult?.data?.total_items,
          priceResult?.data?.product_changes
        )

        setProbe({
          loading: false,
          showInventory,
          showPrice,
          probeFailed: false,
        })
      } catch {
        if (cancelled) return
        setProbe({
          loading: false,
          showInventory: true,
          showPrice: true,
          probeFailed: true,
        })
      }
    }

    runProbe()

    return () => {
      cancelled = true
    }
  }, [
    dispatch,
    resolvedVariantId,
    routePlatformId,
    fetchedConnectedPlatformData.length,
    fetchedConnectedPlatformLoading,
  ])

  const showInventoryTab = probe.probeFailed || probe.showInventory
  const showPriceTab = probe.probeFailed || probe.showPrice
  const showLogsCard = probe.loading || showInventoryTab || showPriceTab

  const tabLabels = useMemo(() => {
    const tabs: { key: 'inventory' | 'price'; label: string }[] = []
    if (probe.loading || showInventoryTab) {
      tabs.push({ key: 'inventory', label: 'Inventory Logs' })
    }
    if (probe.loading || showPriceTab) {
      tabs.push({ key: 'price', label: 'Price Logs' })
    }
    return tabs
  }, [probe.loading, showInventoryTab, showPriceTab])

  useEffect(() => {
    if (tabLabels.length === 0) return
    if (!tabLabels.some((tab) => tab.key === activeTabKey)) {
      setActiveTabKey(tabLabels[0].key)
    }
  }, [tabLabels, activeTabKey, resolvedVariantId])

  const selectedTabIndex = Math.max(
    0,
    tabLabels.findIndex((tab) => tab.key === activeTabKey)
  )

  if (!showLogsCard) return null

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #ddd',
        }}
      >
        {tabLabels.length > 0 ? (
          <Box sx={{ borderBottom: '1px solid #e0e0e0', px: 2, pt: 1 }}>
            <StyledTabs
              value={selectedTabIndex}
              onChange={(_event, index) => {
                const next = tabLabels[index]?.key
                if (next) setActiveTabKey(next)
              }}
            >
              {tabLabels.map((tab) => (
                <StyledTab key={tab.key} label={tab.label} />
              ))}
            </StyledTabs>
          </Box>
        ) : null}
        <CardContent sx={{ p: 2 }}>
          {probe.loading ? (
            <div className="text-muted fs-7 py-4 text-center">Loading logs...</div>
          ) : null}
          {!probe.loading && activeTabKey === 'inventory' && showInventoryTab ? (
            <InventoryLogs isProductDetailPage variantId={resolvedVariantId} />
          ) : null}
          {!probe.loading && activeTabKey === 'price' && showPriceTab ? (
            <PriceLogs isProductDetailPage variantId={resolvedVariantId} />
          ) : null}
        </CardContent>
      </Card>
    </Box>
  )
}
